<script setup>
import FormError from "@/Components/UI/forms/FormError.vue";
import cn from "@/helpers/cn.js";
import FloatLabel from "primevue/floatlabel";
import Password from "primevue/password";
import { computed, ref, useAttrs, useId } from "vue";

const props = defineProps({
    modelValue: { type: String, required: true },
    label: { type: String, default: "Пароль" },
    class: String,
    error: String,
    // sm | md | lg
    size: {
        type: String,
        default: "md",
        validator: (v) => ["sm", "md", "lg"].includes(v),
    },
    // Canonical validity FLAG; falls back to message presence when omitted.
    invalid: { type: Boolean, default: undefined, validator: (v) => typeof v === "boolean" },
});
defineEmits(["update:modelValue"]);

defineOptions({ inheritAttrs: false });

const pw = ref(null);
defineExpose({
    focus() {
        pw.value?.$el?.querySelector("input")?.focus();
    },
});

const inputId = useId();
const isInvalid = computed(() => (props.invalid !== undefined ? props.invalid : !!props.error));

// autocomplete must go through inputProps — PrimeVue routes fallthrough to the root div, not the input.
// feedback:false means there is no strength overlay, so the combobox ARIA PrimeVue always stamps on the
// input (aria-expanded/controls/haspopup) is spurious and trips axe (aria-allowed-attr on a textbox).
// inputProps is merged AFTER those attrs, so null-ing them here removes them from the DOM.
const attrs = useAttrs();
const inputProps = computed(() => ({
    autocomplete: attrs.autocomplete,
    "aria-expanded": null,
    "aria-controls": null,
    "aria-haspopup": null,
}));
</script>

<template>
    <div :class="cn(props.class)">
        <FloatLabel variant="on" class="w-full">
            <Password
                ref="pw"
                :input-id="inputId"
                :model-value="modelValue"
                :toggle-mask="true"
                :feedback="false"
                :fluid="true"
                :invalid="isInvalid"
                :data-size="size"
                :input-props="inputProps"
                class="w-full"
                v-bind="$attrs"
                @update:model-value="(val) => $emit('update:modelValue', val)" />
            <label :for="inputId">{{ label }}</label>
        </FloatLabel>
        <FormError v-if="error" :error="error" />
    </div>
</template>
